import type {
  FinancialSnapshotDocument,
  FinancialSnapshotResponse,
  FinancialSnapshotTemplate,
  NetCashflowStatus,
  SnapshotCategoryData,
  SnapshotMetrics,
  SnapshotStatus,
  SnapshotSubCategoryData,
} from '../financial-snapshot.model';
import { MacroCategory } from '../financial-snapshot.model';

const OUTDATED_DAYS = 90;

function isEnteredNumber(value: unknown): value is number {
  return typeof value === 'number' && !Number.isNaN(value);
}

/** Category total: sum entered subcategory values, else totalValue if entered. */
export function computeCategoryTotal(categoryData: SnapshotCategoryData | undefined): number | null {
  if (!categoryData) {
    return null;
  }
  const subs = categoryData.subCategories;
  if (subs && Object.keys(subs).length > 0) {
    let sum = 0;
    let any = false;
    for (const entry of Object.values(subs)) {
      const v = (entry as SnapshotSubCategoryData).value;
      if (isEnteredNumber(v)) {
        sum += v;
        any = true;
      }
    }
    return any ? sum : null;
  }
  return isEnteredNumber(categoryData.totalValue) ? categoryData.totalValue : null;
}

export function computeHouseholdIncome(
  snapshot: FinancialSnapshotDocument,
  template: FinancialSnapshotTemplate
): number | null {
  let sum = 0;
  let any = false;
  for (const [key, tmpl] of Object.entries(template)) {
    if (tmpl.macroCategory !== MacroCategory.income) {
      continue;
    }
    const total = computeCategoryTotal(snapshot.categories[key]);
    if (total !== null) {
      sum += total;
      any = true;
    }
  }
  return any ? sum : null;
}

export function computeMonthlyCosts(
  snapshot: FinancialSnapshotDocument,
  template: FinancialSnapshotTemplate
): number | null {
  let sum = 0;
  let any = false;
  for (const [key, tmpl] of Object.entries(template)) {
    if (tmpl.macroCategory !== MacroCategory.spending) {
      continue;
    }
    const total = computeCategoryTotal(snapshot.categories[key]);
    if (total !== null) {
      sum += total;
      any = true;
    }
  }
  return any ? sum : null;
}

function netCashflowStatus(net: number | null): NetCashflowStatus | null {
  if (net === null) {
    return null;
  }
  if (net > 0) {
    return 'surplus';
  }
  if (net < 0) {
    return 'shortfall';
  }
  return 'break_even';
}

export function computeMetrics(
  snapshot: FinancialSnapshotDocument,
  template: FinancialSnapshotTemplate
): SnapshotMetrics {
  const householdIncome = computeHouseholdIncome(snapshot, template);
  const monthlyCosts = computeMonthlyCosts(snapshot, template);
  const netCashflow =
    householdIncome === null || monthlyCosts === null ? null : householdIncome - monthlyCosts;
  const costsVsIncomePercent =
    householdIncome === null || householdIncome <= 0 || monthlyCosts === null
      ? null
      : Math.round((monthlyCosts / householdIncome) * 100);

  return {
    householdIncome,
    monthlyCosts,
    netCashflow,
    netCashflowStatus: netCashflowStatus(netCashflow),
    costsVsIncomePercent,
  };
}

function percentOfIncome(amount: number | null, householdIncome: number | null): number | null {
  if (amount === null || householdIncome === null || householdIncome <= 0) {
    return null;
  }
  return Math.round((amount / householdIncome) * 100);
}

export function enrichCategoriesWithSpendingMix(
  snapshot: FinancialSnapshotDocument,
  template: FinancialSnapshotTemplate
): Record<string, SnapshotCategoryData> {
  const householdIncome = computeHouseholdIncome(snapshot, template);
  const out: Record<string, SnapshotCategoryData> = {};

  for (const [key, data] of Object.entries(snapshot.categories)) {
    const tmpl = template[key];
    const copy: SnapshotCategoryData = { ...data };
    if (tmpl?.macroCategory === MacroCategory.spending) {
      copy.percentOfIncome = percentOfIncome(computeCategoryTotal(data), householdIncome);
    }
    out[key] = copy;
  }
  return out;
}

export function deriveStatus(snapshot: FinancialSnapshotDocument, now = new Date()): SnapshotStatus {
  if (!snapshot.submittedDate) {
    return 'in_progress';
  }
  const submitted = new Date(snapshot.submittedDate).getTime();
  const ageMs = now.getTime() - submitted;
  const ageDays = ageMs / (1000 * 60 * 60 * 24);
  return ageDays > OUTDATED_DAYS ? 'outdated' : 'complete';
}

/** Recompute status / metrics / percentOfIncome for mock + live-preview. */
export function recomputeSnapshot(
  snapshot: FinancialSnapshotDocument,
  template: FinancialSnapshotTemplate,
  now = new Date()
): FinancialSnapshotResponse {
  const metrics = computeMetrics(snapshot, template);
  const categories = enrichCategoriesWithSpendingMix(snapshot, template);
  return {
    guid: snapshot.guid,
    createdDate: snapshot.createdDate,
    modifiedDate: snapshot.modifiedDate,
    submittedDate: snapshot.submittedDate,
    lastUpdatedAt: snapshot.lastUpdatedAt,
    status: deriveStatus(snapshot, now),
    categories,
    metrics,
  };
}

/**
 * Progress = UI rows with ≥1 entered value.
 * 10 rows: household income + 7 sub-itemized spending + Other + Savings.
 */
export const FINANCIAL_SNAPSHOT_UI_ROW_COUNT = 10;

const OTHER_NAMED_SUBKEYS = ['activities', 'personal', 'medical'] as const;

function categoryHasEnteredValue(data: SnapshotCategoryData | undefined): boolean {
  if (!data) {
    return false;
  }
  if (isEnteredNumber(data.totalValue)) {
    return true;
  }
  if (!data.subCategories) {
    return false;
  }
  return Object.values(data.subCategories).some((s) =>
    isEnteredNumber((s as SnapshotSubCategoryData).value)
  );
}

export type SnapshotRowEditor = 'fa' | 'client';

export interface SnapshotUiProgressRow {
  id: string;
  label: string;
  entered: boolean;
  /** Sum of entered values for the row; null when pending / blank. */
  amount: number | null;
  /** Shown as a source chip when entered (design: "Client"). */
  source: SnapshotRowEditor | null;
}

const SPENDING_CATEGORY_KEYS = [
  'housing',
  'utilities',
  'transportation',
  'food',
  'dependentcare',
  'subscriptions',
  'debtrepayment',
] as const;

function categorySource(data: SnapshotCategoryData | undefined): SnapshotRowEditor | null {
  if (!data) {
    return null;
  }
  if (data.lastEditedBy === 'client' || data.lastEditedBy === 'fa') {
    return data.lastEditedBy;
  }
  for (const sub of Object.values(data.subCategories ?? {})) {
    const by = (sub as SnapshotSubCategoryData).lastEditedBy;
    if (by === 'client' || by === 'fa') {
      return by;
    }
  }
  return null;
}

function progressRow(
  id: string,
  label: string,
  entered: boolean,
  amount: number | null,
  source: SnapshotRowEditor | null
): SnapshotUiProgressRow {
  return {
    id,
    label,
    entered,
    amount: entered ? amount : null,
    source: entered ? source : null,
  };
}

/** Prefer short UI label ("Activities") over long template instructions. */
function shortOtherLabel(label: string | undefined, fallback: string): string {
  if (!label) {
    return fallback;
  }
  const paren = label.indexOf('(');
  return paren > 0 ? label.slice(0, paren).trim() : label;
}

export interface SpendingMixSubItem {
  key: string;
  label: string;
  amount: number | null;
}

export interface SpendingMixRow {
  key: string;
  label: string;
  amount: number | null;
  percentOfIncome: number | null;
  /** True when the card may show an expand chevron (sub-itemized categories). */
  expandable: boolean;
  /** Subcategory lines shown when the row is expanded on the card. */
  subItems: SpendingMixSubItem[];
}

/** Named template subs + custom labeled entries under `other`. */
export function listOtherSubItems(
  data: SnapshotCategoryData | undefined,
  template: FinancialSnapshotTemplate
): SpendingMixSubItem[] {
  const items: SpendingMixSubItem[] = [];
  const named = template['other']?.subCategories ?? {};
  const namedEntries = Object.entries(named)
    .filter(([key]) => (OTHER_NAMED_SUBKEYS as readonly string[]).includes(key))
    .sort((a, b) => a[1].order - b[1].order);
  for (const [key, tmpl] of namedEntries) {
    const raw = (data?.subCategories?.[key] as SnapshotSubCategoryData | undefined)?.value;
    items.push({
      key,
      label: shortOtherLabel(tmpl.label, key),
      amount: isEnteredNumber(raw) ? raw : null,
    });
  }
  for (const [key, entry] of Object.entries(data?.subCategories ?? {})) {
    if ((OTHER_NAMED_SUBKEYS as readonly string[]).includes(key)) {
      continue;
    }
    const sub = entry as SnapshotSubCategoryData;
    const label =
      typeof sub.label === 'string' && sub.label.trim()
        ? sub.label.trim()
        : shortOtherLabel(undefined, key);
    items.push({
      key,
      label,
      amount: isEnteredNumber(sub.value) ? sub.value : null,
    });
  }
  return items;
}

/** 10 UI rows for progress checklist (income + 7 spending + Other + savings). */
export function listUiProgressRows(
  snapshot: FinancialSnapshotDocument | null | undefined,
  template: FinancialSnapshotTemplate
): SnapshotUiProgressRow[] {
  const cats = snapshot?.categories ?? {};
  const other = cats['other'];
  const income = cats['monthlyhouseholdincome'];
  const rows: SnapshotUiProgressRow[] = [
    progressRow(
      'monthlyhouseholdincome',
      template['monthlyhouseholdincome']?.name ?? 'Household income',
      categoryHasEnteredValue(income),
      computeCategoryTotal(income),
      categorySource(income)
    ),
  ];
  for (const key of SPENDING_CATEGORY_KEYS) {
    const data = cats[key];
    rows.push(
      progressRow(
        key,
        template[key]?.name === 'Debt Repayment' ? 'Debt payments' : template[key]?.name ?? key,
        categoryHasEnteredValue(data),
        computeCategoryTotal(data),
        categorySource(data)
      )
    );
  }
  rows.push(
    progressRow(
      'other',
      template['other']?.name ?? 'Other',
      categoryHasEnteredValue(other),
      computeCategoryTotal(other),
      categorySource(other)
    ),
    progressRow(
      'savings',
      template['savings']?.name ?? 'Savings',
      categoryHasEnteredValue(cats['savings']),
      computeCategoryTotal(cats['savings']),
      categorySource(cats['savings'])
    )
  );
  return rows;
}

export function countEnteredUiRows(
  snapshot: FinancialSnapshotDocument | null | undefined,
  template: FinancialSnapshotTemplate
): number {
  return listUiProgressRows(snapshot, template).filter((r) => r.entered).length;
}

function mixPercent(amount: number | null, householdIncome: number | null): number | null {
  return percentOfIncome(amount, householdIncome);
}

function listCategorySubItems(
  catKey: string,
  data: SnapshotCategoryData | undefined,
  template: FinancialSnapshotTemplate
): SpendingMixSubItem[] {
  const subs = template[catKey]?.subCategories;
  if (!subs) {
    return [];
  }
  return Object.entries(subs)
    .sort((a, b) => a[1].order - b[1].order)
    .map(([key, sub]) => {
      const raw = (data?.subCategories?.[key] as SnapshotSubCategoryData | undefined)?.value;
      return {
        key,
        label: sub.label,
        amount: isEnteredNumber(raw) ? raw : null,
      };
    });
}

/**
 * Spending mix for the card: 7 sub-itemized categories + expandable Other (named + customs).
 * Excludes savings. Single bar color (no High/Med/Low).
 */
export function listSpendingMixRows(
  snapshot: FinancialSnapshotDocument | null | undefined,
  template: FinancialSnapshotTemplate
): SpendingMixRow[] {
  if (!snapshot) {
    return [];
  }
  const householdIncome = computeHouseholdIncome(snapshot, template);
  const rows: SpendingMixRow[] = [];

  for (const key of SPENDING_CATEGORY_KEYS) {
    const tmpl = template[key];
    if (!tmpl) {
      continue;
    }
    const data = snapshot.categories[key];
    const amount = computeCategoryTotal(data);
    rows.push({
      key,
      label: tmpl.name === 'Debt Repayment' ? 'Debt payments' : tmpl.name,
      amount,
      percentOfIncome: mixPercent(amount, householdIncome),
      expandable: true,
      subItems: listCategorySubItems(key, data, template),
    });
  }

  const other = snapshot.categories['other'];
  const otherAmount = computeCategoryTotal(other);
  rows.push({
    key: 'other',
    label: template['other']?.name ?? 'Other',
    amount: otherAmount,
    percentOfIncome: mixPercent(otherAmount, householdIncome),
    expandable: true,
    subItems: listOtherSubItems(other, template),
  });

  return rows;
}

/** Household income subcategory breakdown for the card monthly summary expand. */
export function listIncomeSubItems(
  snapshot: FinancialSnapshotDocument | null | undefined,
  template: FinancialSnapshotTemplate
): SpendingMixSubItem[] {
  if (!snapshot) {
    return [];
  }
  return listCategorySubItems('monthlyhouseholdincome', snapshot.categories['monthlyhouseholdincome'], template);
}
